import { useEffect, useId, useRef, useState } from 'react';
import { InstitutionalDiscoveryError, type DiscoverInstitutionalConnection, type InstitutionalDiscovery } from '@/lib/polymarketUs/institutionalDiscoveryClient';
import { InstitutionalReferenceError, type CheckInstitutionalReference, type InstitutionalReference } from '@/lib/polymarketUs/institutionalReferenceClient';
import { InstitutionalReferenceDetails } from './InstitutionalReferenceDetails';
import type { UsOrgAccount } from './polymarketUsOrgAccounts';

export interface InstitutionalConnectionDraft {
  environment: '' | 'preprod' | 'prod';
  clientId: string; participantFirm: string; participantId: string;
  userName: string; accountName: string;
}
const empty: InstitutionalConnectionDraft = { environment: '', clientId: '', participantFirm: '',
  participantId: '', userName: '', accountName: '' };
const connectionFields = [['clientId', 'API client ID'], ['participantFirm', 'Participant firm'], ['participantId', 'Participant header']] as const;
const inputClass = 'bg-card border-border text-foreground w-full min-w-0 rounded-md border px-2 py-1.5 text-xs';

export function InstitutionalUsAccountSetup({ account, busy, discoveryAvailable, referenceAvailable = false, checkReference, write, discover, onClose }: {
  account: UsOrgAccount | null;
  busy: boolean;
  discoveryAvailable: boolean;
  referenceAvailable?: boolean;
  checkReference?: CheckInstitutionalReference;
  write: (key: string, method: string, path: string, body?: unknown) => Promise<UsOrgAccount | null>;
  discover: DiscoverInstitutionalConnection;
  onClose: () => void;
}) {
  const existing = account?.institutional;
  const unavailableId = useId();
  const [draft, setDraft] = useState<InstitutionalConnectionDraft>(() => existing ? {
    environment: existing.environment, clientId: existing.clientId, participantFirm: existing.participantFirm,
    participantId: existing.participantId, userName: existing.userName, accountName: existing.accountName,
  } : { ...empty });
  const [privateKey, setPrivateKey] = useState('');
  const [fileEpoch, setFileEpoch] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [discovery, setDiscovery] = useState<InstitutionalDiscovery | null>(null);
  const [checking, setChecking] = useState(false);
  const [referenceSymbol, setReferenceSymbol] = useState('');
  const [reference, setReference] = useState<InstitutionalReference | null>(null);
  const [referenceError, setReferenceError] = useState<string | null>(null);
  const [readingReference, setReadingReference] = useState(false);
  const [confirmReplacement, setConfirmReplacement] = useState(false);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const fileRead = useRef(0);
  const checkSequence = useRef(0);
  const checkController = useRef<AbortController | null>(null);
  const referenceController = useRef<AbortController | null>(null);
  const referenceSequence = useRef(0);
  const created = useRef<string | null>(account?.id ?? null);
  useEffect(() => { alive.current = true; return () => {
    alive.current = false; checkController.current?.abort(); referenceController.current?.abort();
  }; }, []);
  const rotating = !!account?.credential;
  const fixedBinding = !!existing || rotating;
  const identity = fixedBinding ? existing?.expectedIdentity : discovery?.identity;
  const rosterReady = !!discovery?.users?.includes(draft.userName) && !!discovery.accounts?.includes(draft.accountName);
  const connectionReady = !!draft.environment && !!draft.clientId.trim() && !!draft.participantFirm.trim() && !!privateKey;
  const ready = connectionReady && !!identity && !!draft.participantId.trim()
    && (fixedBinding ? !!draft.userName && !!draft.accountName : discoveryAvailable && rosterReady)
    && (!rotating || confirmReplacement);
  const locked = busy || saving;

  const invalidateReference = () => {
    referenceSequence.current++; referenceController.current?.abort(); referenceController.current = null;
    setReadingReference(false); setReference(null); setReferenceError(null);
  };
  const invalidateDiscovery = () => {
    invalidateReference();
    checkSequence.current++; checkController.current?.abort(); checkController.current = null;
    setChecking(false); setDiscovery(null); setError(null);
    if (!fixedBinding) setDraft(previous => ({ ...previous, userName: '', accountName: '' }));
  };
  const changeConnection = (key: keyof InstitutionalConnectionDraft, value: string) => {
    if (locked || fixedBinding) return;
    invalidateDiscovery();
    setDraft(previous => ({ ...previous, [key]: value, userName: '', accountName: '' }));
  };
  const pickKey = async (file?: File) => {
    if (locked) return;
    invalidateDiscovery();
    const sequence = ++fileRead.current;
    setPrivateKey('');
    if (!file) return;
    if (file.size > 16 * 1024 || file.size === 0) {
      setError('Choose a nonempty PEM file no larger than 16 KB.'); return;
    }
    try {
      const text = await file.text();
      if (alive.current && sequence === fileRead.current) setPrivateKey(text);
    } catch {
      if (alive.current && sequence === fileRead.current) setError('Could not read this key file.');
    }
  };

  const checkConnection = async () => {
    if (!discoveryAvailable || !connectionReady || locked || checking || readingReference || fixedBinding || checkController.current) return;
    const controller = new AbortController(), sequence = ++checkSequence.current;
    checkController.current = controller;
    setChecking(true); setDiscovery(null); setError(null);
    setDraft(previous => ({ ...previous, userName: '', accountName: '' }));
    try {
      const result = await discover({ environment: draft.environment as 'preprod' | 'prod',
        clientId: draft.clientId.trim(), participantFirm: draft.participantFirm.trim(), privateKey,
        ...(draft.participantId.trim() ? { participantId: draft.participantId.trim() } : {}) }, controller.signal);
      if (alive.current && sequence === checkSequence.current && !controller.signal.aborted) setDiscovery(result);
    } catch (failure) {
      if (alive.current && sequence === checkSequence.current && !controller.signal.aborted) {
        setError(failure instanceof InstitutionalDiscoveryError ? failure.message : 'Could not check the connection. Try again.');
      }
    } finally {
      if (alive.current && sequence === checkSequence.current) { setChecking(false); checkController.current = null; }
    }
  };

  const readReference = async () => {
    if (!referenceAvailable || !checkReference || !connectionReady || !referenceSymbol.trim()
      || locked || checking || readingReference || referenceController.current) return;
    const controller = new AbortController(), sequence = ++referenceSequence.current;
    referenceController.current = controller;
    setReadingReference(true); setReference(null); setReferenceError(null);
    try {
      const result = await checkReference({ environment: draft.environment as 'preprod' | 'prod',
        clientId: draft.clientId.trim(), participantFirm: draft.participantFirm.trim(), privateKey,
        symbol: referenceSymbol.trim() }, controller.signal);
      if (alive.current && sequence === referenceSequence.current && !controller.signal.aborted) setReference(result);
    } catch (failure) {
      if (alive.current && sequence === referenceSequence.current && !controller.signal.aborted) {
        setReferenceError(failure instanceof InstitutionalReferenceError ? failure.message : 'Could not check this instrument. Try again.');
      }
    } finally {
      if (alive.current && sequence === referenceSequence.current) { setReadingReference(false); referenceController.current = null; }
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || !identity || locked || checking || readingReference || inFlight.current) return;
    inFlight.current = true;
    invalidateReference();
    const secret = privateKey;
    setPrivateKey(''); setFileEpoch(k => k + 1); fileRead.current++;
    setSaving(true);
    try {
      let id = created.current;
      if (!id) {
        const row = await write('institutional-create', 'POST', '/accounts', { ownership: 'pooled', protocol: 'institutional' });
        if (!alive.current || !row) return;
        id = row.id; created.current = id;
      }
      if (!alive.current) return;
      const saved = await write(id, 'POST', `/accounts/${id}/institutional-connection`, {
        environment: draft.environment, clientId: draft.clientId.trim(), participantFirm: draft.participantFirm.trim(),
        expectedIdentity: identity, participantId: draft.participantId.trim(),
        userName: draft.userName, accountName: draft.accountName,
        privateKey: secret, ...(rotating ? { replace: true } : {}),
      });
      if (alive.current && saved) onClose();
    } finally { inFlight.current = false; if (alive.current) setSaving(false); }
  };

  return <form onSubmit={save} className="bg-muted/50 border-border mb-3 space-y-3 rounded-md border p-3"
    aria-label="Institutional US connection">
    <p className="text-xs">Connections are saved with trading disabled. Verify the saved connection separately.</p>
    <div className="grid grid-cols-2 gap-3">
      <label className="min-w-0 text-[11px] text-muted-foreground">
        Environment
        <select aria-label="Environment" className={inputClass} required disabled={locked || fixedBinding}
          value={draft.environment} onChange={e => changeConnection('environment', e.target.value)}>
          <option value="">Choose environment</option><option value="preprod">Preproduction</option><option value="prod">Production</option>
        </select>
      </label>
      {connectionFields.map(([key, label]) => <label key={key} className="min-w-0 text-[11px] text-muted-foreground">
        {label}{key === 'participantId' && !fixedBinding ? ' (optional for read-only checks)' : ''}
        <input aria-label={label} className={inputClass} value={draft[key]} required={key !== 'participantId'}
          maxLength={key === 'participantId' ? 2048 : 256} disabled={locked || fixedBinding}
          autoComplete="off" spellCheck={false} onChange={e => changeConnection(key, e.target.value)} />
      </label>)}
    </div>
    {!fixedBinding && <p className="text-muted-foreground text-[11px]">
      Use your provisioned participant header to discover trading users and accounts. It is not inferred from the API identity.
    </p>}
    <label className="block text-[11px] text-muted-foreground">
      RSA private key (.pem)
      <input key={fileEpoch} aria-label="RSA private key (.pem)" type="file" accept=".pem,.key" disabled={locked}
        className={inputClass} onChange={e => { void pickKey(e.target.files?.[0]); }} />
    </label>
    {!fixedBinding && <>
      <button type="button" onClick={() => { void checkConnection(); }} disabled={!discoveryAvailable || !connectionReady || locked || checking || readingReference}
        aria-describedby={!discoveryAvailable ? unavailableId : undefined}
        className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">
        {checking ? 'Checking…' : 'Check connection'}
      </button>
      {!discoveryAvailable && <p id={unavailableId} className="text-muted-foreground text-[11px]">
        Connection checking is unavailable on this site.{!referenceAvailable ? ' No key upload is needed yet.' : ''}
      </p>}
    </>}
    <details className="min-w-0 space-y-2 text-[11px]">
      <summary className="cursor-pointer text-muted-foreground">Optional native instrument check · read only</summary>
      <p className="text-muted-foreground">Use an exact native symbol supplied by Polymarket US. A public market name or slug is not a verified native mapping. No participant header or trading account is required.</p>
      <label className="block min-w-0 text-muted-foreground">
        Native instrument symbol
        <input aria-label="Native instrument symbol" className={inputClass} value={referenceSymbol} maxLength={256}
          autoComplete="off" spellCheck={false} disabled={locked}
          onChange={e => { invalidateReference(); setReferenceSymbol(e.target.value); }} />
      </label>
      <button type="button" onClick={() => { void readReference(); }}
        disabled={!referenceAvailable || !checkReference || !connectionReady || !referenceSymbol.trim() || locked || checking || readingReference}
        className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">
        {readingReference ? 'Checking instrument…' : 'Check instrument'}
      </button>
      {!referenceAvailable && <p className="text-muted-foreground">Instrument checking is unavailable on this site. It is independent of account discovery.</p>}
      {referenceError && <p role="alert" className="text-red-500">{referenceError}</p>}
      {reference && <InstitutionalReferenceDetails reference={reference} />}
    </details>
    {identity && <details className="text-muted-foreground min-w-0 text-[11px]">
      <summary className="cursor-pointer">{fixedBinding ? 'Saved API identity' : 'Connection checked · API identity'}</summary>
      <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2">
        {(['user', 'firm', 'audience'] as const).map(key => <div key={key} className="contents">
          <dt>{key === 'user' ? 'API user' : key === 'firm' ? 'API firm' : 'Audience'}</dt>
          <dd className="min-w-0 break-all">{identity[key]}</dd>
        </div>)}
      </dl>
    </details>}
    {(fixedBinding || discovery) && <div className="grid grid-cols-2 gap-3">
      {([['userName', 'Trading user name', discovery?.users], ['accountName', 'Trading account name', discovery?.accounts]] as const)
        .map(([key, label, choices]) => <label key={key} className="min-w-0 text-[11px] text-muted-foreground">
          {label}
          <select aria-label={label} className={inputClass} value={draft[key]} required disabled={locked || fixedBinding || !choices?.length}
            onChange={e => setDraft(previous => ({ ...previous, [key]: e.target.value }))}>
            {fixedBinding ? <option value={draft[key]}>{draft[key]}</option> : <>
              <option value="">{choices === null ? 'Participant header required' : choices?.length ? 'Choose one' : 'None returned'}</option>
              {choices?.map(value => <option key={value} value={value}>{value}</option>)}
            </>}
          </select>
        </label>)}
    </div>}
    {discovery && !fixedBinding && (discovery.users === null || discovery.accounts === null) && <p className="text-muted-foreground text-[11px]">
      Identity check complete. Add the provisioned participant header and check again to select a trading account.
    </p>}
    {discovery && !fixedBinding && (discovery.users?.length === 0 || discovery.accounts?.length === 0) && <p className="text-muted-foreground text-[11px]">
      No selectable trading user or account was returned. Check participant access with your account administrator.
    </p>}
    {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
    {rotating && <label className="flex items-start gap-2 text-xs">
      <input type="checkbox" checked={confirmReplacement} disabled={locked} onChange={e => setConfirmReplacement(e.target.checked)} />
      This replacement belongs to the same institutional trading account. Saved activity stays with this account.
    </label>}
    <div className="flex items-center gap-2">
      <button type="submit" disabled={!ready || locked || checking || readingReference}
        className="rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white disabled:opacity-50">
        {saving ? 'Saving…' : rotating ? 'Replace connection' : 'Save disabled connection'}
      </button>
      <button type="button" onClick={onClose} disabled={locked} className="rounded-md px-3 py-1.5 text-xs hover:bg-muted">Cancel</button>
    </div>
  </form>;
}
